import React from "react";
import NavBar from "../component/NavBar";
import Footer from "../component/Footer";
import Phone from "../component/Phone";
import { adhd1 } from "assets";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faCheckCircle,
  faHeartPulse,
  faHandHoldingMedical,
} from "@fortawesome/free-solid-svg-icons";

const symptoms = [
  "Difficulty sustaining attention",
  "Frequent careless mistakes",
  "Difficulty organizing tasks",
  "Easily distracted",
  "Excessive restlessness",
  "Impulsive decision-making",
];

const treatmentApproach = [
  {
    icon: faBrain,
    title: "Comprehensive Assessment",
    description: "Thorough evaluation to understand your unique ADHD profile",
  },
  {
    icon: faHeartPulse,
    title: "Medication Management",
    description: "Evidence-based pharmacological interventions tailored to your needs",
  },
  {
    icon: faHandHoldingMedical,
    title: "Behavioral Strategies",
    description: "Practical tools and techniques to improve focus and organization",
  },
];

const ADHD = () => {
  return (
    <div className="bg-white">
      <NavBar />
      <Phone />

      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        <img
          className="w-full h-full object-cover"
          src={adhd1}
          alt="ADHD Treatment"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center animate-fade-in">
            <div className="inline-block px-4 py-2 bg-primary-500/90 rounded-full mb-4">
              <span className="text-white font-semibold text-body-sm">Mental Health Service</span>
            </div>
            <h1 className="text-display-sm md:text-display-md lg:text-display-lg text-white font-bold mb-4">
              ADHD Treatment
            </h1>
            <p className="text-body-lg md:text-heading-sm text-white/90 max-w-2xl mx-auto">
              Specialized care for attention and hyperactivity challenges
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-6">
              Understanding ADHD
            </h2>
            <div className="h-1 w-24 bg-primary-500 mx-auto mb-8"></div>
          </div>

          <div className="bg-white rounded-xl shadow-medium p-8 md:p-10">
            <p className="text-body-lg text-neutral-700 leading-relaxed mb-6">
              Attention Deficit Hyperactivity Disorder (ADHD) is a neurodevelopmental condition that affects both children and adults. While commonly associated with childhood, many adults live with undiagnosed ADHD that may have gone unrecognized during their formative years.
            </p>
            <p className="text-body-lg text-neutral-700 leading-relaxed">
              The symptoms frequently involve hyperactivity, inattention, or a combination of both. Our therapeutic approach to ADHD is centered on a comprehensive understanding of your unique condition, coupled with targeted interventions to improve behavioral management and enhance focus for task completion.
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Common ADHD Symptoms
            </h2>
            <p className="text-body-lg text-neutral-600">
              Recognizing the signs is the first step toward effective treatment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((symptom, index) => (
              <div
                key={index}
                className="bg-neutral-50 rounded-xl p-6 hover:bg-primary-50 transition-colors duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-primary-500 text-xl group-hover:text-primary-600 transition-colors"
                    />
                  </div>
                  <p className="text-body-md text-neutral-800 font-medium">
                    {symptom}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Our Treatment Approach
            </h2>
            <p className="text-body-lg text-neutral-600 max-w-2xl mx-auto">
              Personalized care combining evidence-based strategies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {treatmentApproach.map((approach, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 p-8 text-center group"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-500 transition-colors">
                  <FontAwesomeIcon
                    icon={approach.icon}
                    className="text-2xl text-primary-600 group-hover:text-white transition-colors"
                  />
                </div>
                <h3 className="text-heading-sm text-neutral-900 mb-3">
                  {approach.title}
                </h3>
                <p className="text-body-md text-neutral-600 leading-relaxed">
                  {approach.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-heading-xl md:text-display-sm text-white mb-6">
            Take Control of Your ADHD
          </h2>
          <p className="text-body-lg text-white/90 mb-8 leading-relaxed">
            Don't let ADHD hold you back. Our specialized treatment can help you thrive in work, relationships, and daily life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-neutral-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Schedule Consultation
              </button>
            </Link>
            <Link to="/mainservicepage">
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ADHD;
